import slugify from '@sindresorhus/slugify';

import { config } from '@/lib/config';

type AddressCompletionType = 'city' | 'street' | 'zipcode' | 'City' | 'Street' | 'ZipCode';

/**
 * Returns URL for address completions
 * @param {AddressCompletionType} type - Address completion type
 * @param {Record<string, unknown>} payload - Query parameters
 * @returns {string} Complete URL for address completion
 */
export function getAddressCompletionUrl(
	type: AddressCompletionType,
	payload: Record<string, unknown>
): string {
	const validTypes = ['city', 'street', 'zipcode'];

	if (!validTypes.includes(type.toLowerCase())) {
		throw Error('Invalid completion type specified');
	}

	const urlParams = new URLSearchParams(payload as Record<string, string>).toString();

	const addressCompletionUrlBase = import.meta.env.VITE_APP_ADDRESS_COMPLETION_URL_BASE;
	return `${addressCompletionUrlBase}/${type}/?${urlParams}`;
}

/**
 * Returns bundle reference for product name
 * @param {string} slug - Product slug
 * @returns {string} Bundle reference
 */
export function getBundleRef(slug: string): string {
	const env = import.meta.env.VITE_APP_ENVIRONMENT as 'development' | 'production';
	const envConfig = config[env];

	if ('bundleRefs' in envConfig) {
		const bundleRefs = Object.keys(envConfig.bundleRefs);
		const bundleRef = bundleRefs.filter(
			(item) => slugify(config.bundleNames[item as keyof typeof config.bundleNames]) === slug
		);
		const firstRef = bundleRef[0] as keyof typeof envConfig.bundleRefs;

		return bundleRef?.length && firstRef ? envConfig.bundleRefs[firstRef] : slug;
	}

	return slug;
}

export type TierSelection = 'single' | 'monthly' | 'yearly';
export type TierValues = { low: string; mid: string; high: string };

/**
 * Returns tier values based on selection period
 * @param {TierSelection} selection - Selection period
 * @returns {TierValues} Tier values object
 */
export function getTiers(selection: TierSelection): TierValues {
	switch (selection) {
		case 'single':
			return {
				low: '25',
				mid: '50',
				high: '100'
			};

		case 'monthly':
			return {
				low: '10',
				mid: '15',
				high: '25'
			};

		case 'yearly':
			return {
				low: '50',
				mid: '100',
				high: '250'
			};

		default:
			throw Error('Unsupported period selected', selection);
	}
}

/**
 * @return {String}
 */
export function getIframeUrl(): string {
	if (import.meta.env.VITE_APP_PAYMENT_IFRAME_URL) {
		return import.meta.env.VITE_APP_PAYMENT_IFRAME_URL;
	}

	throw Error('No eligible iFrame URL found');
}

/**
 * Returns URL for payment iFrame, considering free purchases and bundle reference
 * @param {string} bundleRef - Bundle reference
 * @param {boolean} isFreePurchase - Whether this is a free purchase
 * @returns {string} iFrame URL with parameters
 */
export function getIframeUrlWithBundleRef(bundleRef: string, isFreePurchase: boolean): string {
	return `${getIframeUrl()}/${isFreePurchase ? 'free-purchase' : 'payment'}?bundle=${bundleRef}`;
}

/**
 * Returns pretty name for a payment method
 * @param {string} paymentMethod - Payment method identifier
 * @returns {string} Formatted payment method name
 */
export function mapPaymentMethod(paymentMethod: string): string {
	switch (String(paymentMethod).toLowerCase()) {
		case 'creditcard':
		case 'creditdebitcard':
			return 'Kreditkarte';

		case 'paypal':
			return 'PayPal-Adresse';

		case 'directdebit':
		case 'sepa':
			return 'IBAN';

		default:
			throw Error('Unsupported payment method');
	}
}

/**
 * Returns the human-readable checkout URL for a given bundle key
 * @param {keyof typeof config.bundleNames} key - Bundle name key from config
 * @returns {string} The product URL path
 */
export function getBundleUrl(key: keyof typeof config.bundleNames): string {
	return `${config.routes.product}/${slugify(config.bundleNames[key])}`;
}
